import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { BenachrichtigungenApi } from '@elbklar/ui';
import { sitzungErneuern, sitzungsStand, zurAnmeldung } from '@/features/auth/sitzung';

// Anfrage mit Vermerk für die Erneuerung der Sitzung (siehe `bei401`).
type MitSitzung = InternalAxiosRequestConfig & { _sitzungsStand?: number; _wiederholt?: boolean };

// Create axios instance with base configuration
export const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor - add auth token if available
api.interceptors.request.use(
  (config: MitSitzung) => {
    // Token wird via Cookie gesendet (withCredentials: true). Vermerkt wird
    // nur, auf welchem Stand der Sitzung die Anfrage losging.
    config._sitzungsStand = sitzungsStand();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * 401: Meist ist nur das Access-Cookie abgelaufen (15 min), die Sitzung gilt
 * per Refresh noch. Deshalb erst erneuern und die Anfrage EINMAL wiederholen;
 * zur Anmeldung nur, wenn core die Erneuerung ablehnt oder die Wiederholung
 * erneut 401 bekommt. Gleichzeitige 401er teilen sich einen Refresh
 * (`sitzungErneuern`). Der Refresh selbst läuft über `fetch` an core-api,
 * nie über diesen Client, und kann deshalb nicht selbst hier landen.
 */
async function bei401(error: AxiosError) {
  const config = error.config as MitSitzung | undefined;
  if (!config || config._wiederholt) {
    console.warn('[API] Unauthorized - redirecting to login');
    zurAnmeldung();
    throw error;
  }
  // Seit dem Absenden schon erneuert (die 401 kam nach dem Refresh an):
  // nicht noch einmal erneuern, nur wiederholen.
  const ergebnis = config._sitzungsStand !== sitzungsStand() ? 'erneuert' : await sitzungErneuern();
  if (ergebnis === 'erneuert') {
    config._wiederholt = true;
    return api.request(config);
  }
  if (ergebnis === 'abgelehnt') {
    console.warn('[API] Sitzung abgelaufen - redirecting to login');
    zurAnmeldung();
  }
  // `nicht_erreichbar`: Netz oder core gestört. Die Anmeldeseite hälfe nicht
  // und nähme die Eingaben mit; der Fehler geht an den Aufrufer.
  throw error;
}

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ detail?: string; message?: string }>) => {
    const status = error.response?.status;
    const message = error.response?.data?.detail || error.response?.data?.message || error.message;

    // Handle specific status codes
    switch (status) {
      case 401:
        return bei401(error);

      case 403:
        // Forbidden - user doesn't have permission
        console.warn('[API] Forbidden:', message);
        break;

      case 404:
        // Not found - resource doesn't exist
        console.warn('[API] Not found:', error.config?.url);
        break;

      case 422:
        // Validation error
        console.warn('[API] Validation error:', message);
        break;

      case 500:
      case 502:
      case 503:
        // Server error - log for monitoring
        console.error('[API] Server error:', status, message);
        break;

      default:
        // Network error or other
        if (!error.response) {
          console.error('[API] Network error:', error.message);
        }
    }

    return Promise.reject(error);
  }
);

// Helper function to check if error is an API error
export function isApiError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

// Helper to extract error message
export function getApiErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const data = error.response?.data as { detail?: string; message?: string } | undefined;
    return data?.detail || data?.message || error.message || 'Ein unbekannter Fehler ist aufgetreten';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Ein unbekannter Fehler ist aufgetreten';
}

// Ein Validierungsfehler von FastAPI (422) kommt als Liste: je Eintrag
// `loc` (Pfad zum Feld), `msg` und `type`. Hier wird daraus ein lesbarer
// Satz je Feld, etwa „genehmigung_aufwand_stunden: Decimal input should have
// no more than 2 decimal places“. Ein String-`detail` (404, 409) geht
// unverändert durch; ohne beides der Fallback (Gegenprüfung G-16).
export function fehlerText(error: unknown, fallback: string): string {
  if (!isApiError(error)) return error instanceof Error && error.message ? error.message : fallback;
  const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const zeilen = detail
      .map((e: { loc?: unknown[]; msg?: string }) => {
        const feld = Array.isArray(e.loc) ? e.loc.filter((t) => t !== 'body').join('.') : '';
        return feld && e.msg ? `${feld}: ${e.msg}` : e.msg || '';
      })
      .filter(Boolean);
    if (zeilen.length) return zeilen.join(' · ');
  }
  return fallback;
}

// Für das Modul Benachrichtigungen aus @elbklar/ui: derselbe Client wie alle
// übrigen Aufrufe (Basis /api, Cookie, bei 401 erst erneuern, dann zur Anmeldung).
export const benachrichtigungenApi: BenachrichtigungenApi = {
  get: (pfad, params) => api.get(pfad, { params }).then((r) => r.data),
  post: (pfad, daten) => api.post(pfad, daten).then((r) => r.data),
  put: (pfad, daten) => api.put(pfad, daten).then((r) => r.data),
};

export default api;
